import { HazardAnswer } from '@/components/WaterHazard/types';
export type { HazardAnswer };

const apiBase = import.meta.env.ECOS_API_BASE;

export type HazardSurveyStatus =
  | 'NEW'
  | 'IN_PROGRESS'
  | 'IN_REVIEW'
  | 'ACCEPTED'
  | 'CANCELLED';

export type HazardSurveyData = {
  survey: {
    jobId: number;
    instructions: string | null;
    otherHazardsQuestion: boolean;
    protectedQuestion: boolean;
    status: HazardSurveyStatus;
    submitterName: string | null;
    submitterPhone: string | null;
    submitterEmail: string | null;
    otherHazardsAnswer: HazardAnswer | null;
    comments: string | null;
    items: Array<{
      waterHazardId: string;
      answer: HazardAnswer | null;
      isProtected: 'YES' | 'NO' | 'UNKNOWN' | null;
      backflowAssemblyId: number | null;
      waterHazard: {
        id: string;
        name: string;
        description?: string | null;
        hazardLevel?: string | null;
      };
    }>;
    waterService: {
      id: string;
      siteNumber: string | null;
      meterNumber: string | null;
      meterSize?: number | null;
      address: {
        street1: string;
        street2?: string | null;
      };
      backflowAssemblies: any[];
      waterServiceHazards: Array<{
        waterHazardId: string;
        isProtected: 'YES' | 'NO' | 'UNKNOWN' | null;
        backflowAssemblyId: number | null;
        waterHazard: {
          id: string;
          name: string;
          hazardLevel: 'LOW' | 'HIGH';
        };
      }>;
    };
  };
  status: HazardSurveyStatus | null;
  branding: { name: string; logoUrl: string | null } | null;
};

export async function getHazardSurvey(
  token: string
): Promise<HazardSurveyData> {
  const res = await fetch(`${apiBase}/hazard-survey/${token}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? 'Failed to load survey');
  }
  return res.json();
}

export type HazardSurveyAttachment = {
  id: number | string;
  url: string;
  thumb: string | null;
  contentType: string | null;
  type?: string;
  profile?: { id: string; name: string | null } | null;
  created?: number | null;
};

export async function getHazardSurveyAttachments(
  token: string
): Promise<HazardSurveyAttachment[]> {
  const res = await fetch(`${apiBase}/hazard-survey/${token}/attachments`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? 'Failed to load attachments');
  }
  return res.json();
}

export async function getHazardSurveyUploadUrl(
  token: string,
  contentType: string
): Promise<{ url: string; key: string }> {
  const res = await fetch(
    `${apiBase}/hazard-survey/${token}/upload-url?contentType=${encodeURIComponent(contentType)}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? 'Failed to get upload URL');
  }
  return res.json();
}

export async function addHazardSurveyAttachment(
  token: string,
  tempKey: string,
  contentType: string
): Promise<void> {
  const res = await fetch(`${apiBase}/hazard-survey/${token}/attachment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tempKey, contentType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? 'Failed to add attachment');
  }
}

export type HazardSurveySubmitPayload = {
  submitterName?: string;
  submitterPhone?: string;
  submitterEmail?: string;
  hazards?: Record<string, HazardAnswer>;
  protectedByHazard?: Record<string, HazardAnswer | null>;
  backflowAssemblyByHazard?: Record<string, string | null>;
  otherHazards?: HazardAnswer;
  comments?: string;
};

export async function submitHazardSurvey(
  token: string,
  formData: HazardSurveySubmitPayload
): Promise<void> {
  const res = await fetch(`${apiBase}/hazard-survey/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? 'Failed to submit survey');
  }
}
